import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { X, User, Calendar, Phone, Mail, MessageSquare, Clock, Loader2, AlertCircle, MapPin, CalendarDays } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactModal = ({ isOpen, onClose, onSuccess, sessionId, appointmentData = {} }) => {
  const hasAppointment = appointmentData?.date && appointmentData?.time;
  
  const [formData, setFormData] = useState({
    vorname: "",
    nachname: "",
    alter: "",
    wohnort: "",
    startbereit: "",
    telefon: "",
    erreichbarkeit: "",
    email: "",
    nachricht: ""
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Refs for scrolling to error fields
  const fieldRefs = {
    vorname: useRef(null),
    nachname: useRef(null),
    alter: useRef(null),
    wohnort: useRef(null),
    startbereit: useRef(null),
    telefon: useRef(null),
    email: useRef(null)
  };

  const validateForm = () => {
    const newErrors = {};
    const fieldOrder = ['vorname', 'nachname', 'alter', 'startbereit', 'telefon', 'email'];
    
    if (!formData.vorname.trim()) {
      newErrors.vorname = "Pflichtfeld";
    }
    if (!formData.nachname.trim()) {
      newErrors.nachname = "Pflichtfeld";
    }
    if (!formData.alter.trim()) {
      newErrors.alter = "Pflichtfeld";
    } else if (isNaN(Number(formData.alter)) || Number(formData.alter) < 16 || Number(formData.alter) > 99) {
      newErrors.alter = "Gültiges Alter (16-99)";
    }
    if (!formData.startbereit.trim()) {
      newErrors.startbereit = "Pflichtfeld";
    }
    if (!formData.telefon.trim()) {
      newErrors.telefon = "Pflichtfeld";
    } else if (!/^[\d\s\-+()]{6,20}$/.test(formData.telefon)) {
      newErrors.telefon = "Ungültige Nummer";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Pflichtfeld";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Ungültige E-Mail";
    }

    setErrors(newErrors);
    
    // Scroll to first error field
    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = fieldOrder.find(field => newErrors[field]);
      if (firstErrorField && fieldRefs[firstErrorField]?.current) {
        fieldRefs[firstErrorField].current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        // Focus the input after scroll
        setTimeout(() => {
          const input = fieldRefs[firstErrorField].current?.querySelector('input');
          if (input) input.focus();
        }, 300);
      }
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Combine erreichbarkeit into message
      const combinedMessage = formData.erreichbarkeit 
        ? `Beste Erreichbarkeit: ${formData.erreichbarkeit}${formData.nachricht ? '\n\nNachricht: ' + formData.nachricht : ''}`
        : formData.nachricht || "";
      
      const response = await axios.post(`${API}/contact`, {
        first_name: `${formData.vorname} ${formData.nachname}`,
        age: parseInt(formData.alter),
        wohnort: formData.wohnort,
        start_date: formData.startbereit,
        phone: formData.telefon,
        email: formData.email,
        message: combinedMessage,
        session_id: sessionId
      });
      
      // Different success messages based on whether there's an appointment
      if (hasAppointment) {
        toast.success(
          <div>
            <strong>Termin bestätigt! 🎉</strong>
            <p className="text-sm mt-1">Du erhältst eine E-Mail mit allen Infos.</p>
          </div>
        );
      } else {
        toast.success(
          <div>
            <strong>Bewerbung gesendet! 👍</strong>
            <p className="text-sm mt-1">Lea meldet sich schnellstmöglich bei dir.</p>
          </div>
        );
      }
      
      onSuccess(hasAppointment ? 'appointment' : 'callback');
      
      // Reset form
      setFormData({
        vorname: "",
        nachname: "",
        alter: "",
        wohnort: "",
        startbereit: "",
        telefon: "",
        erreichbarkeit: "",
        email: "",
        nachricht: ""
      });
      setErrors({});
    } catch (error) {
      console.error("Error submitting contact:", error);
      toast.error("Es gab einen Fehler. Bitte versuche es erneut.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Error field styling
  const getInputClassName = (fieldName) => {
    const baseClass = "rounded-lg transition-all duration-200";
    if (errors[fieldName]) {
      return `${baseClass} border-2 border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-500/20 animate-shake`;
    }
    return baseClass;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="contact-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {hasAppointment ? "Fast geschafft! 🎉" : "Kontaktdaten eintragen"}
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            {hasAppointment 
              ? "Füll noch kurz deine Daten aus und du bekommst alle Infos per E-Mail."
              : "Lea meldet sich schnellstmöglich telefonisch bei dir."
            }
          </DialogDescription>
        </DialogHeader>

        {/* Appointment Summary */}
        {hasAppointment && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 mt-2">
            <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
              <CalendarDays className="w-5 h-5" />
              Dein Termin
            </h4>
            <div className="space-y-1 text-sm text-green-700">
              <p className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span className="font-medium">{appointmentData.date}</span>
              </p>
              <p className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span className="font-medium">{appointmentData.time}</span>
              </p>
              <p className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span className="font-medium">Düsseldorf (Hauptstelle)</span>
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="contact-form">
          {/* Vorname */}
          <div className="space-y-1" ref={fieldRefs.vorname}>
            <Label htmlFor="vorname" className={`text-sm font-medium flex items-center gap-2 ${errors.vorname ? 'text-red-600' : 'text-slate-700'}`}>
              <User className={`w-4 h-4 ${errors.vorname ? 'text-red-500' : 'text-slate-400'}`} />
              Vorname *
              {errors.vorname && <span className="text-red-500 text-xs ml-auto flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.vorname}</span>}
            </Label>
            <Input
              id="vorname"
              value={formData.vorname}
              onChange={handleChange("vorname")}
              className={getInputClassName("vorname")}
              data-testid="input-vorname"
            />
          </div>

          {/* Nachname */}
          <div className="space-y-1" ref={fieldRefs.nachname}>
            <Label htmlFor="nachname" className={`text-sm font-medium flex items-center gap-2 ${errors.nachname ? 'text-red-600' : 'text-slate-700'}`}>
              <User className={`w-4 h-4 ${errors.nachname ? 'text-red-500' : 'text-slate-400'}`} />
              Nachname *
              {errors.nachname && <span className="text-red-500 text-xs ml-auto flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.nachname}</span>}
            </Label>
            <Input
              id="nachname"
              value={formData.nachname}
              onChange={handleChange("nachname")}
              className={getInputClassName("nachname")}
              data-testid="input-nachname"
            />
          </div>

          {/* Alter */}
          <div className="space-y-1" ref={fieldRefs.alter}>
            <Label htmlFor="alter" className={`text-sm font-medium flex items-center gap-2 ${errors.alter ? 'text-red-600' : 'text-slate-700'}`}>
              <Calendar className={`w-4 h-4 ${errors.alter ? 'text-red-500' : 'text-slate-400'}`} />
              Alter *
              {errors.alter && <span className="text-red-500 text-xs ml-auto flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.alter}</span>}
            </Label>
            <Input
              id="alter"
              type="number"
              min="16"
              max="99"
              value={formData.alter}
              onChange={handleChange("alter")}
              className={getInputClassName("alter")}
              data-testid="input-alter"
            />
          </div>

          {/* Wohnort */}
          <div className="space-y-1" ref={fieldRefs.wohnort}>
            <Label htmlFor="wohnort" className="text-sm font-medium flex items-center gap-2 text-slate-700">
              <MapPin className="w-4 h-4 text-slate-400" />
              Wohnort
            </Label>
            <Input
              id="wohnort"
              value={formData.wohnort}
              onChange={handleChange("wohnort")}
              className="rounded-lg"
              data-testid="input-wohnort"
            />
          </div>

          {/* Startbereit */}
          <div className="space-y-1" ref={fieldRefs.startbereit}>
            <Label htmlFor="startbereit" className={`text-sm font-medium flex items-center gap-2 ${errors.startbereit ? 'text-red-600' : 'text-slate-700'}`}>
              <Clock className={`w-4 h-4 ${errors.startbereit ? 'text-red-500' : 'text-slate-400'}`} />
              Ab wann startklar? *
              {errors.startbereit && <span className="text-red-500 text-xs ml-auto flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.startbereit}</span>}
            </Label>
            <Input
              id="startbereit"
              value={formData.startbereit}
              onChange={handleChange("startbereit")}
              className={getInputClassName("startbereit")}
              data-testid="input-startbereit"
            />
          </div>

          {/* Telefon */}
          <div className="space-y-1" ref={fieldRefs.telefon}>
            <Label htmlFor="telefon" className={`text-sm font-medium flex items-center gap-2 ${errors.telefon ? 'text-red-600' : 'text-slate-700'}`}>
              <Phone className={`w-4 h-4 ${errors.telefon ? 'text-red-500' : 'text-slate-400'}`} />
              Telefonnummer *
              {errors.telefon && <span className="text-red-500 text-xs ml-auto flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.telefon}</span>}
            </Label>
            <Input
              id="telefon"
              type="tel"
              value={formData.telefon}
              onChange={handleChange("telefon")}
              className={getInputClassName("telefon")}
              data-testid="input-telefon"
            />
          </div>

          {/* Email */}
          <div className="space-y-1" ref={fieldRefs.email}>
            <Label htmlFor="email" className={`text-sm font-medium flex items-center gap-2 ${errors.email ? 'text-red-600' : 'text-slate-700'}`}>
              <Mail className={`w-4 h-4 ${errors.email ? 'text-red-500' : 'text-slate-400'}`} />
              E-Mail-Adresse *
              {errors.email && <span className="text-red-500 text-xs ml-auto flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.email}</span>}
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={handleChange("email")}
              className={getInputClassName("email")}
              data-testid="input-email"
            />
          </div>

          {/* Erreichbarkeit - optional, no placeholder */}
          <div className="space-y-1">
            <Label htmlFor="erreichbarkeit" className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-400" />
              Beste Erreichbarkeit
            </Label>
            <Input
              id="erreichbarkeit"
              value={formData.erreichbarkeit}
              onChange={handleChange("erreichbarkeit")}
              className="rounded-lg"
              data-testid="input-erreichbarkeit"
            />
          </div>

          {/* Nachricht - optional, no placeholder */}
          <div className="space-y-1">
            <Label htmlFor="nachricht" className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-slate-400" />
              Nachricht (optional)
            </Label>
            <Textarea
              id="nachricht"
              value={formData.nachricht}
              onChange={handleChange("nachricht")}
              rows={3}
              className="rounded-lg resize-none"
              data-testid="input-nachricht"
            />
          </div>

          {/* Privacy note */}
          <p className="text-xs text-slate-400 text-center">
            {hasAppointment 
              ? "Du erhältst eine Bestätigung mit Anreiseinfos per E-Mail."
              : "Deine Daten werden ausschließlich zur Kontaktaufnahme verwendet."
            }
          </p>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white rounded-full py-6 text-base font-semibold shadow-lg shadow-blue-500/30 btn-primary-hover"
            data-testid="submit-contact-button"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Wird gesendet...
              </>
            ) : hasAppointment ? (
              "Termin bestätigen & absenden"
            ) : (
              "Absenden – Lea ruft an"
            )}
          </Button>
        </form>
        
        {/* CSS for shake animation */}
        <style>{`
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
            20%, 40%, 60%, 80% { transform: translateX(4px); }
          }
          .animate-shake {
            animation: shake 0.5s ease-in-out;
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;
